import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-key-value-row>` says about itself.
 *
 * ── `interactive` IS AN AFFORDANCE, NOT A BEHAVIOUR ──────────────────────────
 * It changes how the value LOOKS on hover — a dashed accent underline that hugs the value, the
 * inline-edit idiom — and nothing else. There is no click handling, no editing and no event: the
 * component has no idea what the row is meant to do. Setting it on a row that does nothing when
 * clicked is the failure it invites, so it is stated in the input, in `whenToUse`, and again under
 * accessibility, where it costs the most.
 *
 * ── ATTRIBUTES THE STYLESHEET READS AND THE SCRIPT DOES NOT ──────────────────
 * `observedAttributes` is `label` and `value` — the two the class writes into the DOM. `stacked`,
 * `interactive` and `no-border` are read by the stylesheet through `:host([…])` selectors, so they
 * need no callback and are real inputs all the same. There is a fourth, `empty`, which is NOT an
 * input: the component sets and clears it itself to show or hide the em dash, and an author who
 * sets it by hand is fighting the component.
 */
const docs: ComponentDocs = {
  aliases: ['field row', 'label value pair', 'definition row', 'detail row', 'property row', 'read-only field'],

  answers: [
    "show one of a record's fields without letting it be edited",
    'lay a label and its value out the way every other field on the page is laid out',
    'show that a field has no value rather than leaving a gap',
    'build a field-group card of stacked label-over-value cells',
  ],

  placements: ['recordPage', 'appPage'],

  inputs: [
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'What the field is called. Drawn as a mono uppercase kicker in the muted ink. A label ' +
        'slotted into the label slot is drawn after this text, so use one or the other.',
    },
    {
      name: 'value',
      attribute: 'value',
      type: 'text',
      description:
        'The value, as a plain string — already formatted, because this piece formats nothing. ' +
        'Slotted content is drawn beside it and is what to use for a value that is not plain text. ' +
        'With neither, the row shows an em dash.',
    },
    {
      name: 'stacked',
      attribute: 'stacked',
      type: 'boolean',
      description:
        'Put the label ABOVE the value instead of beside it. This is the field-group cell — several ' +
        'stacked rows laid into a grid by whatever holds them makes a field-group card.',
      default: false,
    },
    {
      name: 'interactive',
      attribute: 'interactive',
      type: 'boolean',
      description:
        'Make the value LOOK editable or linkable: a pointer, and a dashed accent underline that ' +
        'hugs the value on hover. Appearance only — it adds no click handling and raises no event, ' +
        'so the surface has to make the row actually do something.',
      default: false,
    },
    {
      name: 'noBorder',
      attribute: 'no-border',
      type: 'boolean',
      description:
        'Drop the hairline under the row. Set it on the last row of a group, where the rule would ' +
        "otherwise draw a line across the bottom of the section's content.",
      default: false,
    },
  ],

  // Declared as NONE rather than left unsaid: the row is read-only detail. Even with `interactive`
  // set it raises nothing — see the note in the header about what that attribute does and does not
  // do.
  events: [],

  state: [],

  slots: [
    {
      name: '',
      takes:
        'The value, when it is not plain text: a StatusPill, a Tag, a link, a ProvenanceMarker, or a ' +
        'SkeletonBlock standing in while it loads. Anything here suppresses the em dash.',
    },
    {
      name: 'label',
      takes:
        'The label, when it needs to be more than a string — a label with a Tooltip or an InlineHint ' +
        'beside it. Drawn after any text set through the label attribute.',
    },
  ],

  tokens: [
    { token: '--caos-font-mono', controls: 'The face of the label kicker.' },
    { token: '--caos-font-weight-label', controls: 'The weight of the label.' },
    { token: '--caos-color-text-muted', controls: 'The label ink, and the em dash on an empty row.' },
    { token: '--caos-font', controls: 'The face of the value.' },
    { token: '--caos-text-body', controls: 'The size of the value.' },
    { token: '--caos-color-text', controls: 'The value ink.' },
    { token: '--caos-color-border', controls: 'The hairline under the row.' },
    { token: '--caos-color-accent', controls: 'The underline and ink an interactive value takes on hover.' },
    { token: '--caos-space-4', controls: 'The gap between the label column and the value.' },
    { token: '--caos-space-2', controls: 'The padding above and below the row.' },
    { token: '--caos-space-1', controls: 'The gap between label and value in the stacked variant.' },
  ],

  parts: [
    { part: 'row', is: 'the row' },
    { part: 'label', is: 'the label side' },
    { part: 'value', is: 'the value side' },
  ],

  methods: [],

  placing:
    'A row goes inside a DetailSection, stacked with the other rows of its group — it draws no card ' +
    'and no padding of its own beyond its own line. Beside its label it takes a third of the width ' +
    '(at least 120px) and gives the rest to the value; under 480px it stacks by itself, whatever ' +
    'the stacked attribute says. The stacked variant is the cell of a field-group grid, and the ' +
    'grid belongs to whatever holds the rows.',

  whenToUse:
    'Showing one field of a record that is being READ. If the value can be changed here, it is a ' +
    'field rather than a row, and a field piece should draw it — the interactive attribute makes a ' +
    'row look editable without making it editable.',

  insteadUse: [
    {
      name: 'FieldRenderer',
      when:
        'The value can be EDITED, or its type should decide how it draws. That piece reads or edits ' +
        "whatever it is handed, in that field's own type.",
    },
    {
      name: 'DetailSection',
      when: 'You are placing the group rather than the row — the titled card these sit inside.',
    },
    {
      name: 'StatTile',
      when: 'The value is a headline figure being read on its own rather than one field among many.',
    },
    {
      name: 'DataTable',
      when:
        'The same fields are being read across several records. A column of rows repeated per record ' +
        'is a table drawn the hard way.',
    },
  ],

  accessibility:
    'The label and the value are read in order as ordinary text, and a row with no value is read as ' +
    'an em dash rather than as silence — so an empty field is not mistaken for a missing one. What ' +
    'is left to the author is the label wording and, above all, the interactive attribute: it is ' +
    'appearance only, so a row that looks editable but is not is announced as plain text and reached ' +
    'by nobody using a keyboard. If the row is meant to do something, the thing that does it has to ' +
    'be a real control placed in the value slot.',

  examples: [
    {
      key: 'rows',
      label: 'Rows of a record',
      summary:
        'Four fields as they read down a section, the last of them with no value so the em dash ' +
        'stands in and the last rule dropped.',
      placement: [
        { component: 'caos-key-value-row', inputs: { label: 'Account', value: 'Meridian Supply' } },
        { component: 'caos-key-value-row', inputs: { label: 'Reference', value: 'ORD-4471' } },
        { component: 'caos-key-value-row', inputs: { label: 'Total', value: '12,400' } },
        { component: 'caos-key-value-row', inputs: { label: 'Purchase order', 'no-border': true } },
      ],
    },
    {
      key: 'stacked',
      label: 'Stacked, as a field group',
      summary:
        'The same pairs with the label over the value — the cell shape a field-group card is built ' +
        'from, which is a different reading order from the row above and not just a narrower one.',
      placement: [
        { component: 'caos-key-value-row', inputs: { label: 'Account', value: 'Meridian Supply', stacked: true } },
        { component: 'caos-key-value-row', inputs: { label: 'Stage', value: 'Draft', stacked: true } },
        { component: 'caos-key-value-row', inputs: { label: 'Owner', value: 'D. Whitfield', stacked: true } },
      ],
    },
    {
      key: 'slotted',
      label: 'A value that is not text',
      summary:
        'A pill in the value slot instead of a string, and beside it a row marked interactive — ' +
        'which only LOOKS editable on hover, and needs the surface to make it do something.',
      placement: [
        {
          component: 'caos-key-value-row',
          inputs: { label: 'Stage' },
          children: [{ component: 'caos-status-pill', inputs: { intent: 'muted' }, children: ['Draft'] }],
        },
        {
          component: 'caos-key-value-row',
          inputs: { label: 'Owner', value: 'D. Whitfield', interactive: true, 'no-border': true },
        },
      ],
    },
  ],
};

export default docs;
